import type { AnyRouter } from '@tanstack/react-router';
import { findExactRoute, getFlatRoutes } from '@/routing/flat-routes';
import type { AppRouteMeta } from '@/routing/route-meta';
import { getAppRouteMeta } from '@/routing/router-static-data';

/** The slice of a TanStack route the resolver reads (`getFlatRoutes(router)`). */
export type RouteForTarget = {
  fullPath?: unknown;
  options?: { staticData?: unknown };
  parentRoute?: unknown;
};

export type NavigationTarget =
  { ok: true; href: string; pathname: string } | { ok: false; error: string };

// Only used to parse an in-app href; never navigated to.
const PARSE_ORIGIN = 'http://app.invalid';

function isRouteForTarget(value: unknown): value is RouteForTarget {
  return typeof value === 'object' && value !== null;
}

/**
 * A route's own access metadata, else its nearest ancestor's. Record routes
 * (`/sales-orders-v3/$draftId`) usually declare none and inherit their
 * section's rules; the route's `beforeLoad` stays the real gate.
 */
export function nearestAppRouteMeta(
  route: RouteForTarget,
): AppRouteMeta | undefined {
  let current: unknown = route;
  while (isRouteForTarget(current)) {
    const meta = getAppRouteMeta(current);
    if (meta) return meta;
    current = current.parentRoute;
  }
  return undefined;
}

/**
 * The access rules that apply to a route: its own or a tree ancestor's, else
 * those of the nearest route above it by path. A record page mounted straight
 * under the app layout (`/workbooks/$workbookId` beside `/workbooks`) has no
 * tree ancestor with rules, but belongs to the section its path sits in. The
 * route's `beforeLoad` stays the real gate.
 */
export function routeAccessMeta(
  route: RouteForTarget,
  routes: readonly RouteForTarget[],
): AppRouteMeta | undefined {
  const own = nearestAppRouteMeta(route);
  if (own || typeof route.fullPath !== 'string') return own;
  const byPath = routesByPath(routes);
  const segments = route.fullPath.split('/').filter(Boolean);
  for (let depth = segments.length - 1; depth > 0; depth -= 1) {
    const prefix = `/${segments.slice(0, depth).join('/')}`;
    for (const candidate of byPath.get(prefix) ?? []) {
      const meta = nearestAppRouteMeta(candidate);
      if (meta) return meta;
    }
  }
  return undefined;
}

const pathIndexes = new WeakMap<
  readonly RouteForTarget[],
  Map<string, RouteForTarget[]>
>();

/** Routes by full path, built once per route list (`getFlatRoutes(router)`). */
function routesByPath(
  routes: readonly RouteForTarget[],
): Map<string, RouteForTarget[]> {
  let index = pathIndexes.get(routes);
  if (!index) {
    index = new Map();
    for (const route of routes) {
      if (typeof route.fullPath !== 'string') continue;
      const list = index.get(route.fullPath) ?? [];
      list.push(route);
      index.set(route.fullPath, list);
    }
    pathIndexes.set(routes, index);
  }
  return index;
}

function normalizePathname(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

/**
 * Validates an agent-supplied in-app href against the routes this user can
 * reach. Accepts static pages and concrete record URLs (`/x/123` matches
 * `/x/$id`); rejects external URLs, unknown paths, and routes whose access
 * rules (module, flag, permission, admin) exclude the user.
 */
export function resolveNavigationTarget(
  router: Pick<AnyRouter, 'routesById' | 'getMatchedRoutes'>,
  href: string,
  canAccess: (meta: AppRouteMeta) => boolean,
): NavigationTarget {
  const trimmed = href.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return {
      ok: false,
      error: `"${href}" is not an in-app path. Pass a path starting with "/".`,
    };
  }
  const url = new URL(trimmed, PARSE_ORIGIN);
  const pathname = normalizePathname(url.pathname);

  const route = findExactRoute(router, pathname);
  // A route with no access metadata anywhere up its chain is guarded only in
  // `beforeLoad`; it can't be vetted here, so it isn't offered.
  const meta = route && routeAccessMeta(route, getFlatRoutes(router));
  if (!meta) {
    return {
      ok: false,
      error: `No page exists at ${pathname}. Use find_pages to look up a valid path.`,
    };
  }
  if (!canAccess(meta)) {
    return { ok: false, error: `${pathname} is not available to this user.` };
  }
  return { ok: true, href: `${pathname}${url.search}${url.hash}`, pathname };
}
